import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';

export interface AuthStatus {
  isAuthenticated: boolean;
  user?: any;
  expiresAt?: number;
}

/**
 * This service maintains authentication state without using HttpClient
 * to avoid circular dependencies with HTTP interceptors
 */
@Injectable({
  providedIn: 'root'
})
export class AuthStateService {
  private authStatusSubject = new BehaviorSubject<AuthStatus>({ isAuthenticated: false });
  public authStatus$ = this.authStatusSubject.asObservable();
  
  // Cache control
  private lastCheck = 0;
  private readonly cacheTime = 60000; // 1 minute cache

  constructor(private router: Router) {}

  /**
   * Update the authentication status
   */
  updateAuthStatus(status: AuthStatus): void {
    this.lastCheck = Date.now();
    this.authStatusSubject.next(status);
  }

  /**
   * Get the stored authentication status
   */
  getStoredAuthStatus(): AuthStatus {
    return this.authStatusSubject.value;
  }

  /**
   * Check if we need to refresh auth status (based on cache time)
   */
  needsRefresh(force: boolean = false): boolean {
    const now = Date.now();
    return force || now - this.lastCheck > this.cacheTime || !this.authStatusSubject.value.isAuthenticated;
  }

  /**
   * Navigate to login page with optional redirect URL
   */
  redirectToLogin(redirectUrl?: string): void {
    this.router.navigate(['/login'], { 
      queryParams: { 
        redirectUrl: redirectUrl || this.router.url,
        sessionExpired: 'true' 
      }
    });
  }
}